import React, { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import swal from "sweetalert";
import { FaPlus, FaEdit, FaTrash, FaEye, FaTimes, FaUser, FaEnvelope, FaExclamationTriangle, FaSync, FaUserPlus } from "react-icons/fa";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filters, setFilters] = useState({
    role: ""
  });

  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [form, setForm] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    role_id: ""
  });

  const API_BASE_URL = "https://dairy.server.statify.co.ke/";

  // Color scheme matching employees page
  const colors = {
    primary: "#4361ee",
    secondary: "#3a0ca3",
    success: "#4cc9f0",
    warning: "#f72585",
    error: "#e63946",
    light: "#f8f9fa",
    dark: "#212529",
    background: "#ffffff",
    cardBg: "#ffffff",
    border: "#e9ecef",
    text: "#2b2d42",
    textLight: "#6c757d"
  };

  // Enhanced fetch function with error handling
  const fetchWithTimeout = async (url, options = {}, timeout = 8000) => {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);
    
    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });
      clearTimeout(id);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return response;
    } catch (error) {
      clearTimeout(id);
      throw error;
    }
  };

  /** ----------------- AUTHENTICATION ----------------- **/
  const getToken = () => {
    try {
      const token = localStorage.getItem("token");
      return token && typeof token === 'string' && token.trim() !== '' ? token : null;
    } catch (error) {
      console.error("Error getting token:", error);
      return null;
    }
  };

  const checkAuthentication = () => {
    const token = getToken();
    if (!token) {
      setIsAuthenticated(false);
      setCurrentUser(null);
      return false;
    }
    
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      
      if (decoded.exp && decoded.exp < currentTime) {
        localStorage.removeItem("token");
        setIsAuthenticated(false);
        setCurrentUser(null);
        return false;
      }
      
      // Get user role from token or use default
      const userRole = decoded.role || "user";
      
      console.log("🔐 Decoded token:", decoded);
      console.log("🎯 User role from token:", userRole);
      
      const userInfo = {
        id: decoded.id || decoded.user_id || decoded.sub,
        username: decoded.username || decoded.preferred_username || decoded.name || "User",
        role: userRole,
        allClaims: decoded
      };
      
      console.log("👤 Current user info:", userInfo);
      
      setIsAuthenticated(true);
      setCurrentUser(userInfo);
      return true;
    } catch (error) {
      console.warn("Token decoding failed:", error.message);
      setIsAuthenticated(false);
      setCurrentUser(null);
      return false;
    }
  };

  const isAdmin = () => {
    const userRole = currentUser?.role;
    console.log("🛂 Checking admin status - User role:", userRole);
    
    if (!userRole) {
      console.log("❌ No role found for user");
      return false;
    }
    
    // Check for admin role
    const adminRoles = ['admin', 'administrator', 'superadmin', 'super_user', 'superuser'];
    const isAdminUser = adminRoles.includes(userRole.toLowerCase());
    
    console.log("🔍 Admin check result:", isAdminUser);
    return isAdminUser;
  };

  // Helper function to get role name by ID
  const getRoleName = (roleId) => {
    if (!roleId) return "N/A";
    const role = roles.find(r => r.id === roleId || r.id === parseInt(roleId));
    return role ? role.name : "N/A";
  };

  // Helper function to get role name for display (handles both role_id and role fields)
  const getUserRoleDisplay = (user) => {
    if (user.role) return user.role;
    if (user.role_id) return getRoleName(user.role_id);
    return "N/A";
  };

  /** ----------------- DATA FETCHING ----------------- **/
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = getToken();
      
      if (!token) {
        throw new Error("Authentication required. Please login again.");
      }

      const queryParams = new URLSearchParams();
      if (filters.role) queryParams.append("role", filters.role);

      const url = `${API_BASE_URL}/api/users/?${queryParams.toString()}`;
      const res = await fetchWithTimeout(url, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
      });

      if (res.status === 401 || res.status === 403) {
        localStorage.removeItem("token");
        setIsAuthenticated(false);
        setCurrentUser(null);
        throw new Error("Session expired. Please login again.");
      }

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || errorData.message || "Failed to fetch users");
      }

      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
      setFilteredUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Fetch users error:", err);
      setError(err.message);
      setUsers([]);
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const token = getToken();
      if (!token) {
        console.warn("No token available for roles fetch");
        return;
      }

      // Use the correct endpoint for roles
      const endpoint = `${API_BASE_URL}/api/roles/`;
      console.log(`Fetching roles from: ${endpoint}`);

      const res = await fetchWithTimeout(endpoint, {
        headers: { 
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
      });

      if (res.ok) {
        const data = await res.json();
        console.log("Raw roles data:", data);
        
        if (Array.isArray(data)) {
          console.log("Successfully fetched roles:", data);
          setRoles(data);
        } else if (data && typeof data === 'object') {
          // Handle case where roles might be in a nested property
          if (data.roles && Array.isArray(data.roles)) {
            console.log("Successfully fetched roles from nested property:", data.roles);
            setRoles(data.roles);
          } else if (data.data && Array.isArray(data.data)) {
            console.log("Successfully fetched roles from data property:", data.data);
            setRoles(data.data);
          } else {
            console.warn("Roles data is not in expected format:", data);
            setRoles([]);
          }
        } else {
          console.warn("Roles data is not an array:", data);
          setRoles([]);
        }
      } else {
        console.warn(`Failed to fetch roles: ${res.status}`);
        setRoles([]);
      }
    } catch (err) {
      console.error("Fetch roles error:", err);
      setRoles([]);
    }
  };

  // Fetch current user profile from session
  const fetchCurrentUserProfile = async () => {
    try {
      const token = getToken();
      if (!token) return;

      const endpoint = `${API_BASE_URL}/api/users/profile`;
      console.log(`Fetching current user profile from: ${endpoint}`);

      const res = await fetchWithTimeout(endpoint, {
        headers: { 
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
      });

      if (res.ok) {
        const userData = await res.json();
        console.log("Current user profile:", userData);
        
        // Update current user with data from profile
        if (userData && userData.id) {
          setCurrentUser(prev => ({
            ...prev,
            ...userData,
            // Ensure role is set from profile data
            role: userData.role || prev?.role
          }));
        }
      } else {
        console.warn(`Failed to fetch user profile: ${res.status}`);
      }
    } catch (err) {
      console.error("Fetch user profile error:", err);
    }
  };

  useEffect(() => {
    const init = async () => {
      const authenticated = checkAuthentication();
      if (authenticated) {
        await fetchUsers();
        await fetchRoles();
        await fetchCurrentUserProfile();
      } else {
        setLoading(false);
        setError("Authentication required. Please login to access this page.");
      }
    };
    
    init();
  }, [filters]);

  /** ----------------- SEARCH AND FILTER ----------------- **/
  useEffect(() => {
    if (!Array.isArray(users)) {
      setFilteredUsers([]);
      return;
    }
    
    let results = users;
    
    // Apply search term
    if (searchTerm) {
      results = results.filter(user =>
        (user.username?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (user.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (getUserRoleDisplay(user)?.toLowerCase() || '').includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    if (sortConfig.key) {
      results.sort((a, b) => {
        let aVal, bVal;

        // Handle role sorting specially
        if (sortConfig.key === 'role') {
          aVal = getUserRoleDisplay(a);
          bVal = getUserRoleDisplay(b);
        } else {
          aVal = a[sortConfig.key];
          bVal = b[sortConfig.key];
        }
        
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;
        
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          return sortConfig.direction === 'asc' 
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }
        
        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredUsers(results);
  }, [users, searchTerm, sortConfig, roles]);

  /** ----------------- SORT HANDLER ----------------- **/
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  /** ----------------- FILTER HANDLER ----------------- **/
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  /** ----------------- RESET FILTERS ----------------- **/
  const resetFilters = () => {
    setFilters({
      role: ""
    });
    setSearchTerm("");
    setSortConfig({ key: null, direction: 'asc' });
  };

  /** ----------------- FORM HANDLERS ----------------- **/
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ 
      ...prev, 
      [name]: value 
    }));
  };

  const openDrawer = (user = null, view = false) => {
    if (user) {
      // Convert null values to empty strings for form
      const formData = {};
      Object.keys(form).forEach(key => {
        formData[key] = user[key] !== null && user[key] !== undefined ? user[key] : "";
      });
      
      setForm(formData);
      setEditingUser(user);
      setViewMode(view);
    } else {
      setForm({
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
        role_id: ""
      });
      setEditingUser(null);
      setViewMode(false);
    }
    setDrawerOpen(true);
    setError(null);
    setSuccess(null);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingUser(null);
    setViewMode(false);
    setError(null);
    setSuccess(null);
  };

  const validateForm = () => {
    const requiredFields = ['username', 'email', 'role_id'];
    const missingFields = requiredFields.filter(field => !form[field]?.trim());
    
    if (missingFields.length > 0) {
      setError(`Please fill in required fields: ${missingFields.map(f => f.replace('_', ' ')).join(', ')}`);
      return false;
    }
    
    if (!editingUser && !form.password) {
      setError('Password is required for new users');
      return false;
    }
    
    if (form.password && form.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const token = getToken();
      if (!token) {
        throw new Error("Authentication required. Please login.");
      }

      // Use the register endpoint for new users
      const url = editingUser 
        ? (isAdmin() ? `${API_BASE_URL}/api/users/${editingUser.id}` : `${API_BASE_URL}/api/users/profile`)
        : `${API_BASE_URL}/api/users/register`;

      const method = editingUser ? "PUT" : "POST";

      // Prepare data for submission
      const submitData = { ...form };
      
      // Remove confirmPassword from submission
      delete submitData.confirmPassword;
      
      // Convert empty strings to null
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '') {
          submitData[key] = null;
        }
      });

      // Only include password if provided
      if (!submitData.password) {
        delete submitData.password;
      }

      console.log("Submitting user data:", submitData);
      console.log("URL:", url);
      console.log("Method:", method);

      const res = await fetchWithTimeout(url, {
        method,
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(submitData),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || errorData.message || "Operation failed");
      }

      const data = await res.json();
      
      if (editingUser) {
        setSuccess("User updated successfully!");
      } else {
        setSuccess("User registered successfully!");
      }
      
      await fetchUsers();
      
      setTimeout(() => {
        closeDrawer();
      }, 1500);

    } catch (err) {
      console.error("Error saving user:", err);
      setError(`Failed to save user: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!isAdmin() && currentUser?.id !== id) {
      swal("Error", "You can only delete your own account", "error");
      return;
    }

    const confirm = await swal({
      title: "Are you sure?",
      text: currentUser?.id === id 
        ? "This will permanently delete your account and log you out immediately!" 
        : "This will permanently delete the user!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirm) return;

    try {
      const token = getToken();
      const res = await fetchWithTimeout(`${API_BASE_URL}/api/users/${id}`, { 
        method: "DELETE",
        headers: { 
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (!res.ok) {
        throw new Error("Failed to delete user");
      }

      setSuccess("User deleted successfully!");
      
      if (currentUser?.id === id) {
        // User deleted their own account
        localStorage.removeItem("token");
        setIsAuthenticated(false);
        setCurrentUser(null);
        setTimeout(() => {
          window.location.href = "/login";
        }, 1500);
      } else {
        await fetchUsers();
      }
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Error deleting user:", err);
      setError(`Failed to delete user: ${err.message}`);
    }
  };

  const handleView = (user) => {
    const roleName = getUserRoleDisplay(user);
    const details = `
👤 Username: ${user.username}
📧 Email: ${user.email || 'N/A'}
👥 Role: ${roleName || 'N/A'}

🆔 User ID: ${user.id}
📅 Created: ${user.created_at ? user.created_at.split('T')[0] : 'N/A'}
    `.trim();

    swal({
      title: `User Details - ${user.username}`,
      text: details,
      icon: "info",
      buttons: {
        edit: {
          text: "Edit",
          value: "edit",
        },
        close: {
          text: "Close",
          value: "close",
        }
      }
    }).then((value) => {
      if (value === "edit") {
        openDrawer(user);
      }
    });
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    setIsAuthenticated(false);
    setCurrentUser(null);
    window.location.href = "/login";
  };

  const getFieldIcon = (fieldName) => {
    const icons = {
      username: <FaUser size={14} />,
      email: <FaEnvelope size={14} />,
      role_id: <FaUser size={14} />
    };
    return icons[fieldName] || <FaUser size={14} />;
  };

  // Clear messages after timeout
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  /** ----------------- STYLES ----------------- **/
  const styles = {
    page: {
      minHeight: "100vh", 
      backgroundColor: colors.light,
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
    },
    container: {
      maxWidth: "1400px", 
      margin: "0 auto", 
      padding: "20px" 
    },
    header: {
      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
      borderRadius: "16px",
      padding: "30px",
      marginBottom: "30px",
      color: "white",
      boxShadow: "0 8px 25px rgba(67, 97, 238, 0.3)"
    },
    title: {
      margin: "0 0 10px 0", 
      fontSize: "clamp(1.8rem, 4vw, 2.5rem)",
      fontWeight: "700"
    },
    subtitle: {
      margin: 0, 
      opacity: 0.9,
      fontSize: "clamp(0.9rem, 2vw, 1.1rem)"
    },
    controls: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "25px",
      flexWrap: "wrap",
      gap: "15px"
    },
    searchBox: {
      padding: "10px 15px",
      border: "1px solid #ddd",
      borderRadius: "8px",
      fontSize: "14px",
      minWidth: "250px",
      flex: "1"
    },
    filterSelect: {
      padding: "10px 15px",
      border: "1px solid #ddd",
      borderRadius: "8px",
      fontSize: "14px",
      backgroundColor: "white",
      minWidth: "150px"
    },
    actionButton: {
      backgroundColor: colors.primary,
      color: "white",
      border: "none",
      padding: "12px 24px",
      borderRadius: "12px",
      cursor: "pointer",
      fontWeight: "600",
      fontSize: "14px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      boxShadow: `0 4px 15px ${colors.primary}40`,
      transition: "all 0.3s ease"
    },
    tableContainer: {
      backgroundColor: colors.background,
      borderRadius: "16px",
      overflow: "hidden",
      boxShadow: "0 4px 20px rgba(0,0,0,0.08)"
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      minWidth: "800px"
    },
    tableHeader: {
      backgroundColor: colors.primary,
      color: "white"
    },
    tableHeaderCell: {
      padding: "16px 20px", 
      textAlign: "left", 
      fontWeight: "600",
      fontSize: "14px",
      cursor: "pointer",
      userSelect: "none"
    },
    tableCell: {
      padding: "16px 20px",
      border: "none",
      fontSize: "14px"
    },
    actionButtonSmall: {
      background: "none", 
      border: "none", 
      cursor: "pointer",
      padding: "8px",
      borderRadius: "8px",
      transition: "all 0.2s ease"
    }
  };

  // Debug info
  const debugInfo = `
🔐 Authentication Status: ${isAuthenticated ? '✅ Authenticated' : '❌ Not authenticated'}
👤 Current User: ${currentUser?.username || 'None'}
🎯 User Role: ${currentUser?.role || 'None'}
🛂 Is Admin: ${isAdmin() ? '✅ YES' : '❌ NO'}
🔑 Token Present: ${getToken() ? '✅ Yes' : '❌ No'}
  `.trim();

  console.log("🔍 DEBUG INFO:");
  console.log(debugInfo);

  /** ----------------- RENDER ----------------- **/
  if (!isAuthenticated) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={{ 
            textAlign: "center", 
            padding: "60px 20px",
            color: colors.textLight
          }}>
            <FaExclamationTriangle size={48} style={{ marginBottom: "20px", opacity: 0.5 }} />
            <h2>Authentication Required</h2>
            <p>Please login to access this page.</p>
            <button
              onClick={() => (window.location.href = "/login")}
              style={styles.actionButton}
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.title}>👥 User Management</h1>
          <p style={styles.subtitle}>
            Welcome, {currentUser?.username} ({currentUser?.role}) - Manage system users efficiently
          </p>
          {/* Debug info */}
          <div style={{ 
            marginTop: "10px", 
            padding: "10px", 
            backgroundColor: "rgba(255,255,255,0.2)", 
            borderRadius: "8px",
            fontSize: "12px",
            fontFamily: "monospace"
          }}>
            <div>🛂 Admin Status: {isAdmin() ? '✅ YES' : '❌ NO'}</div>
            <div>👤 Role: {currentUser?.role || 'None'}</div>
            <div>👥 Available Roles: {roles.length}</div>
          </div>
        </div>

        {/* Success Banner */}
        {success && (
          <div style={{
            backgroundColor: `${colors.success}15`,
            border: `1px solid ${colors.success}40`,
            color: colors.success,
            padding: "16px 20px",
            borderRadius: "12px",
            marginBottom: "25px",
            display: "flex",
            alignItems: "center",
            gap: "12px"
          }}>
            <FaSync />
            <div style={{ flex: 1 }}>
              {success}
            </div>
            <button
              onClick={() => setSuccess(null)}
              style={{
                background: "none",
                border: "none",
                color: colors.success,
                cursor: "pointer",
                padding: "4px"
              }}
            >
              <FaTimes />
            </button>
          </div>
        )}

        {/* Error Banner */}
        {error && (
          <div style={{
            backgroundColor: `${colors.error}15`,
            border: `1px solid ${colors.error}40`,
            color: colors.error,
            padding: "16px 20px",
            borderRadius: "12px",
            marginBottom: "25px",
            display: "flex",
            alignItems: "center",
            gap: "12px"
          }}>
            <FaExclamationTriangle />
            <div style={{ flex: 1 }}>
              {error}
            </div>
            <button
              onClick={() => setError(null)}
              style={{
                background: "none",
                border: "none",
                color: colors.error,
                cursor: "pointer",
                padding: "4px"
              }}
            >
              <FaTimes />
            </button>
          </div>
        )}

        {/* Action Bar */}
        <div style={styles.controls}>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap"
          }}>
            <div style={{
              padding: "8px 16px",
              backgroundColor: colors.background,
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              color: colors.text,
              fontWeight: "600"
            }}>
              Total: {users.length} users
            </div>
            
            <input
              type="text"
              placeholder="🔍 Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchBox}
            />
            
            <select 
              value={filters.role} 
              onChange={(e) => handleFilterChange('role', e.target.value)}
              style={styles.filterSelect}
            >
              <option value="">All Roles</option>
              {roles.map(role => (
                <option key={role.id} value={role.name}>{role.name}</option>
              ))}
            </select>

            <button
              onClick={resetFilters}
              style={{
                padding: "10px 15px",
                backgroundColor: colors.textLight,
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "14px"
              }}
            >
              🔄 Reset
            </button>
          </div>
          
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              onClick={fetchUsers}
              style={{
                ...styles.actionButton,
                backgroundColor: colors.success
              }}
              title="Refresh users"
            >
              <FaSync /> Refresh
            </button>
            
            {/* ADD USER BUTTON - Available to all authenticated users */}
            <button
              onClick={() => openDrawer()}
              style={styles.actionButton}
            >
              <FaUserPlus /> Add User
            </button>
            
            <button
              onClick={handleLogout}
              style={{
                ...styles.actionButton,
                backgroundColor: colors.warning
              }}
            >
              Logout
            </button>
          </div>
        </div>

        {/* Users Table */}
        <div style={styles.tableContainer}>
          {loading ? (
            <div style={{ 
              padding: "60px 20px", 
              textAlign: "center", 
              color: colors.textLight 
            }}>
              <FaSync size={24} style={{ animation: "spin 1s linear infinite", marginBottom: "16px" }} />
              <div>Loading users...</div>
            </div>
          ) : error && users.length === 0 ? (
            <div style={{ 
              padding: "60px 20px", 
              textAlign: "center", 
              color: colors.textLight 
            }}>
              <FaExclamationTriangle size={32} style={{ marginBottom: "16px", opacity: 0.5 }} />
              <div>Unable to load users. {error}</div>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tableHeader}>
                    {['USERNAME', 'EMAIL', 'ROLE', 'ACTIONS'].map(header => (
                      <th 
                        key={header}
                        style={styles.tableHeaderCell}
                        onClick={() => handleSort(header.toLowerCase())}
                      >
                        {header} {sortConfig.key === header.toLowerCase() && 
                          (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(filteredUsers) && filteredUsers.length > 0 ? (
                    filteredUsers.map((user, index) => (
                      <tr 
                        key={user.id}
                        style={{
                          backgroundColor: index % 2 === 0 ? colors.background : "#f8f9fe",
                          borderBottom: `1px solid ${colors.border}`,
                          transition: "background-color 0.2s ease"
                        }}
                        onMouseEnter={(e) => {
                          e.target.parentNode.style.backgroundColor = "#f0f2ff";
                        }}
                        onMouseLeave={(e) => {
                          e.target.parentNode.style.backgroundColor = index % 2 === 0 ? colors.background : "#f8f9fe";
                        }}
                      >
                        <td style={styles.tableCell}>
                          <div>
                            <div style={{ 
                              fontWeight: "600", 
                              color: colors.text,
                              marginBottom: "4px"
                            }}>
                              {user.username}
                            </div>
                            {user.first_name && user.last_name && (
                              <div style={{ 
                                fontSize: "12px", 
                                color: colors.textLight 
                              }}>
                                {user.first_name} {user.last_name}
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={styles.tableCell}>{user.email}</td>
                        <td style={styles.tableCell}>{getUserRoleDisplay(user)}</td>
                        <td style={styles.tableCell}>
                          <div style={{
                            display: "flex",
                            gap: "8px"
                          }}>
                            <button
                              onClick={() => handleView(user)}
                              style={{
                                ...styles.actionButtonSmall,
                                color: colors.primary,
                              }}
                              title="View Details"
                              onMouseOver={(e) => {
                                e.target.style.backgroundColor = `${colors.primary}15`;
                              }}
                              onMouseOut={(e) => {
                                e.target.style.backgroundColor = "transparent";
                              }}
                            >
                              <FaEye size={16} />
                            </button>
                            <button
                              onClick={() => openDrawer(user)}
                              style={{
                                ...styles.actionButtonSmall,
                                color: colors.success,
                              }}
                              title="Edit User"
                              onMouseOver={(e) => {
                                e.target.style.backgroundColor = `${colors.success}15`;
                              }}
                              onMouseOut={(e) => {
                                e.target.style.backgroundColor = "transparent";
                              }}
                            >
                              <FaEdit size={16} />
                            </button>
                            {isAdmin() && (
                              <button
                                onClick={() => handleDelete(user.id)}
                                style={{
                                  ...styles.actionButtonSmall,
                                  color: colors.warning,
                                }}
                                title="Delete User"
                                onMouseOver={(e) => {
                                  e.target.style.backgroundColor = `${colors.warning}15`;
                                }}
                                onMouseOut={(e) => {
                                  e.target.style.backgroundColor = "transparent";
                                }}
                              >
                                <FaTrash size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" style={{ 
                        textAlign: "center", 
                        padding: "60px 20px",
                        color: colors.textLight
                      }}>
                        <div style={{ fontSize: "16px", marginBottom: "10px" }}>
                          No users found
                        </div>
                        <button
                          onClick={() => openDrawer()}
                          style={styles.actionButton}
                        >
                          <FaUserPlus /> Add Your First User
                        </button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Drawer */}
      {drawerOpen && (
        <>
          {/* Overlay */}
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1000,
            }}
            onClick={closeDrawer}
          />
          
          {/* Drawer Content */}
          <div
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "min(90%, 500px)",
              height: "100%",
              backgroundColor: colors.background,
              boxShadow: "-4px 0 25px rgba(0,0,0,0.15)",
              padding: "0",
              overflowY: "auto",
              transition: "all 0.3s ease-in-out",
              zIndex: 1001,
              display: "flex",
              flexDirection: "column"
            }}
          >
            {/* Drawer Header */}
            <div style={{
              padding: "25px 25px 20px",
              borderBottom: `1px solid ${colors.border}`,
              background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
              color: "white"
            }}>
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "10px"
              }}>
                <h2 style={{ margin: 0, fontSize: "1.5rem" }}>
                  {viewMode ? "User Details" : editingUser ? "Edit User" : "Add User"}
                </h2>
                <button
                  onClick={closeDrawer}
                  style={{
                    background: "none",
                    border: "none",
                    color: "white",
                    cursor: "pointer",
                    padding: "8px",
                    borderRadius: "8px",
                    transition: "background-color 0.2s ease"
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = "rgba(255,255,255,0.2)";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = "transparent";
                  }}
                >
                  <FaTimes size={20} />
                </button>
              </div>
              <p style={{ margin: 0, opacity: 0.9, fontSize: "14px" }}>
                {viewMode ? "View user information" : 
                 editingUser ? "Update user details" : "Add a new system user"}
              </p>
            </div>

            {/* Drawer Body */}
            <div style={{ padding: "25px", flex: 1 }}>
              <form onSubmit={handleSubmit}>
                <div style={{
                  display: "grid",
                  gap: "20px"
                }}>
                  {['username', 'email', 'role_id'].map((field) => {
                    const readOnly = viewMode;
                    const isRequired = ['username', 'email', 'role_id'].includes(field);
                    const isPassword = field === 'password';

                    return (
                      <div key={field}>
                        <label style={{ 
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "6px", 
                          fontWeight: "600",
                          color: colors.text,
                          fontSize: "14px"
                        }}>
                          {getFieldIcon(field)}
                          {field.split('_').map(word => 
                            word.charAt(0).toUpperCase() + word.slice(1)
                          ).join(' ')}
                          {isRequired && <span style={{ color: colors.error }}>*</span>}
                        </label>
                        
                        {field === 'role_id' ? (
                          <select
                            name={field}
                            value={form[field] || ""}
                            onChange={handleChange}
                            disabled={readOnly || (!isAdmin() && editingUser && currentUser?.id !== editingUser.id)}
                            style={{
                              width: "100%",
                              padding: "12px",
                              border: `1px solid ${colors.border}`,
                              borderRadius: "8px",
                              backgroundColor: (readOnly || (!isAdmin() && editingUser && currentUser?.id !== editingUser.id)) ? "#f8f9fa" : "white",
                              color: colors.text,
                              fontSize: "14px",
                              transition: "all 0.2s ease"
                            }}
                          >
                            <option value="">Select Role</option>
                            {roles.map(role => (
                              <option key={role.id} value={role.id}>{role.name}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type={isPassword ? "password" : "text"}
                            name={field}
                            value={form[field] || ""}
                            onChange={handleChange}
                            readOnly={readOnly}
                            disabled={readOnly || (!isAdmin() && editingUser && currentUser?.id !== editingUser.id)}
                            placeholder={isRequired ? "Required" : ""}
                            style={{
                              width: "100%",
                              padding: "12px",
                              border: `1px solid ${colors.border}`,
                              borderRadius: "8px",
                              backgroundColor: (readOnly || (!isAdmin() && editingUser && currentUser?.id !== editingUser.id)) ? "#f8f9fa" : "white",
                              color: colors.text,
                              fontSize: "14px",
                              transition: "all 0.2s ease",
                              cursor: readOnly ? "not-allowed" : "text"
                            }}
                            onFocus={(e) => {
                              if (!readOnly) {
                                e.target.style.borderColor = colors.primary;
                                e.target.style.boxShadow = `0 0 0 3px ${colors.primary}20`;
                              }
                            }}
                            onBlur={(e) => {
                              if (!readOnly) {
                                e.target.style.borderColor = colors.border;
                                e.target.style.boxShadow = "none";
                              }
                            }}
                          />
                        )}
                      </div>
                    );
                  })}

                  {/* Password Fields (only for add/edit, not view) */}
                  {!viewMode && (
                    <>
                      <div>
                        <label style={{ 
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "6px", 
                          fontWeight: "600",
                          color: colors.text,
                          fontSize: "14px"
                        }}>
                          <FaUser size={14} />
                          Password
                          {!editingUser && <span style={{ color: colors.error }}>*</span>}
                        </label>
                        <input
                          type="password"
                          name="password"
                          value={form.password || ""}
                          onChange={handleChange}
                          placeholder={!editingUser ? "Required" : "Leave blank to keep current"}
                          style={{
                            width: "100%",
                            padding: "12px",
                            border: `1px solid ${colors.border}`,
                            borderRadius: "8px",
                            backgroundColor: "white",
                            color: colors.text,
                            fontSize: "14px",
                            transition: "all 0.2s ease"
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = colors.primary;
                            e.target.style.boxShadow = `0 0 0 3px ${colors.primary}20`;
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = colors.border;
                            e.target.style.boxShadow = "none";
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ 
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "6px", 
                          fontWeight: "600",
                          color: colors.text,
                          fontSize: "14px"
                        }}>
                          <FaUser size={14} />
                          Confirm Password
                          {!editingUser && <span style={{ color: colors.error }}>*</span>}
                        </label>
                        <input
                          type="password"
                          name="confirmPassword"
                          value={form.confirmPassword || ""}
                          onChange={handleChange}
                          placeholder={!editingUser ? "Required" : "Confirm new password"}
                          style={{
                            width: "100%",
                            padding: "12px",
                            border: `1px solid ${colors.border}`,
                            borderRadius: "8px",
                            backgroundColor: "white",
                            color: colors.text,
                            fontSize: "14px",
                            transition: "all 0.2s ease"
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = colors.primary;
                            e.target.style.boxShadow = `0 0 0 3px ${colors.primary}20`;
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = colors.border;
                            e.target.style.boxShadow = "none";
                          }}
                        />
                      </div>
                    </>
                  )}
                </div>

                {!viewMode && (
                  <button
                    type="submit"
                    disabled={submitting}
                    style={{
                      backgroundColor: submitting ? colors.textLight : colors.primary,
                      color: "white",
                      padding: "14px",
                      border: "none",
                      borderRadius: "8px",
                      cursor: submitting ? "not-allowed" : "pointer",
                      width: "100%",
                      fontWeight: "600",
                      fontSize: "15px",
                      marginTop: "30px",
                      transition: "all 0.3s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px"
                    }}
                    onMouseOver={(e) => {
                      if (!submitting) {
                        e.target.style.backgroundColor = colors.secondary;
                        e.target.style.transform = "translateY(-2px)";
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!submitting) {
                        e.target.style.backgroundColor = colors.primary;
                        e.target.style.transform = "translateY(0)";
                      }
                    }}
                  >
                    {submitting ? (
                      <>
                        <FaSync className="spin" size={16} />
                        {editingUser ? "Updating..." : "Adding User..."}
                      </>
                    ) : (
                      <>
                        <FaUserPlus size={16} />
                        {editingUser ? "Update User" : "Add User"}
                      </>
                    )}
                  </button>
                )}
              </form>
            </div>
          </div>
        </>
      )}

      {/* Add CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default Users;





// import React, { useState, useEffect } from "react";
// import { jwtDecode } from "jwt-decode";
// import swal from "sweetalert";
// import { FaPlus, FaEdit, FaTrash, FaEye, FaTimes, FaUser, FaEnvelope, FaExclamationTriangle, FaSync } from "react-icons/fa";

// const Users = () => {
//   const [users, setUsers] = useState([]);
//   const [filteredUsers, setFilteredUsers] = useState([]);
//   const [roles, setRoles] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [drawerOpen, setDrawerOpen] = useState(false);
//   const [viewMode, setViewMode] = useState(false);
//   const [editingUser, setEditingUser] = useState(null);
//   const [error, setError] = useState(null);
//   const [success, setSuccess] = useState(null);
//   const [submitting, setSubmitting] = useState(false);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
//   const [filters, setFilters] = useState({
//     role: ""
//   });

//   const [currentUser, setCurrentUser] = useState(null);
//   const [isAuthenticated, setIsAuthenticated] = useState(false);

//   const [form, setForm] = useState({
//     username: "",
//     email: "",
//     password: "",
//     confirmPassword: "",
//     role_id: ""
//   });

//   const API_BASE_URL = "http://127.0.0.1:5055";

//   // Color scheme matching employees page
//   const colors = {
//     primary: "#4361ee",
//     secondary: "#3a0ca3",
//     success: "#4cc9f0",
//     warning: "#f72585",
//     error: "#e63946",
//     light: "#f8f9fa",
//     dark: "#212529",
//     background: "#ffffff",
//     cardBg: "#ffffff",
//     border: "#e9ecef",
//     text: "#2b2d42",
//     textLight: "#6c757d"
//   };

//   // Enhanced fetch function with error handling
//   const fetchWithTimeout = async (url, options = {}, timeout = 8000) => {
//     const controller = new AbortController();
//     const id = setTimeout(() => controller.abort(), timeout);
    
//     try {
//       const response = await fetch(url, {
//         ...options,
//         signal: controller.signal,
//         headers: {
//           'Content-Type': 'application/json',
//           ...options.headers,
//         },
//       });
//       clearTimeout(id);
      
//       if (!response.ok) {
//         throw new Error(`HTTP error! status: ${response.status}`);
//       }
      
//       return response;
//     } catch (error) {
//       clearTimeout(id);
//       throw error;
//     }
//   };

//   /** ----------------- AUTHENTICATION ----------------- **/
//   const getToken = () => {
//     try {
//       const token = localStorage.getItem("token");
//       return token && typeof token === 'string' && token.trim() !== '' ? token : null;
//     } catch (error) {
//       console.error("Error getting token:", error);
//       return null;
//     }
//   };

//   const checkAuthentication = () => {
//     const token = getToken();
//     if (!token) {
//       setIsAuthenticated(false);
//       setCurrentUser(null);
//       return false;
//     }
    
//     try {
//       const decoded = jwtDecode(token);
//       const currentTime = Date.now() / 1000;
      
//       if (decoded.exp && decoded.exp < currentTime) {
//         localStorage.removeItem("token");
//         setIsAuthenticated(false);
//         setCurrentUser(null);
//         return false;
//       }
      
//       const userInfo = {
//         id: decoded.id || decoded.user_id || decoded.sub,
//         username: decoded.username || decoded.preferred_username || "User",
//         role: decoded.role || "user",
//       };
      
//       setIsAuthenticated(true);
//       setCurrentUser(userInfo);
//       return true;
//     } catch (error) {
//       console.warn("Token decoding failed:", error.message);
//       setIsAuthenticated(false);
//       setCurrentUser(null);
//       return false;
//     }
//   };

//   const isAdmin = () => {
//     return currentUser?.role && currentUser.role.toLowerCase() === "admin";
//   };

//   // Helper function to get role name by ID
//   const getRoleName = (roleId) => {
//     if (!roleId) return "N/A";
//     const role = roles.find(r => r.id === roleId || r.id === parseInt(roleId));
//     return role ? role.name : "N/A";
//   };

//   // Helper function to get role name for display (handles both role_id and role fields)
//   const getUserRoleDisplay = (user) => {
//     if (user.role) return user.role;
//     if (user.role_id) return getRoleName(user.role_id);
//     return "N/A";
//   };

//   /** ----------------- DATA FETCHING ----------------- **/
//   const fetchUsers = async () => {
//     try {
//       setLoading(true);
//       setError(null);
//       const token = getToken();
      
//       if (!token) {
//         throw new Error("Authentication required. Please login again.");
//       }

//       const queryParams = new URLSearchParams();
//       if (filters.role) queryParams.append("role", filters.role);

//       const url = `${API_BASE_URL}/api/users/?${queryParams.toString()}`;
//       const res = await fetchWithTimeout(url, {
//         headers: {
//           "Authorization": `Bearer ${token}`,
//           "Content-Type": "application/json"
//         },
//       });

//       if (res.status === 401 || res.status === 403) {
//         localStorage.removeItem("token");
//         setIsAuthenticated(false);
//         setCurrentUser(null);
//         throw new Error("Session expired. Please login again.");
//       }

//       if (!res.ok) {
//         const errorData = await res.json();
//         throw new Error(errorData.error || errorData.message || "Failed to fetch users");
//       }

//       const data = await res.json();
//       setUsers(Array.isArray(data) ? data : []);
//       setFilteredUsers(Array.isArray(data) ? data : []);
//     } catch (err) {
//       console.error("Fetch users error:", err);
//       setError(err.message);
//       setUsers([]);
//       setFilteredUsers([]);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const fetchRoles = async () => {
//     try {
//       const token = getToken();
//       if (!token) {
//         console.warn("No token available for roles fetch");
//         return;
//       }

//       // Use the working endpoint from your logs
//       const endpoint = `${API_BASE_URL}/api/users/roles`;
//       console.log(`Fetching roles from: ${endpoint}`);

//       const res = await fetchWithTimeout(endpoint, {
//         headers: { 
//           "Authorization": `Bearer ${token}`,
//           "Content-Type": "application/json"
//         },
//       });

//       if (res.ok) {
//         const data = await res.json();
//         if (Array.isArray(data)) {
//           console.log("Successfully fetched roles:", data);
//           setRoles(data);
//         } else {
//           console.warn("Roles data is not an array:", data);
//           setRoles([]);
//         }
//       } else {
//         console.warn(`Failed to fetch roles: ${res.status}`);
//         setRoles([]);
//       }
//     } catch (err) {
//       console.error("Fetch roles error:", err);
//       // Set empty array instead of default roles to avoid confusion
//       setRoles([]);
//     }
//   };

//   useEffect(() => {
//     const init = async () => {
//       const authenticated = checkAuthentication();
//       if (authenticated) {
//         await fetchUsers();
//         await fetchRoles();
//       } else {
//         setLoading(false);
//         setError("Authentication required. Please login to access this page.");
//       }
//     };
    
//     init();
//   }, [filters]);

//   /** ----------------- SEARCH AND FILTER ----------------- **/
//   useEffect(() => {
//     if (!Array.isArray(users)) {
//       setFilteredUsers([]);
//       return;
//     }
    
//     let results = users;
    
//     // Apply search term
//     if (searchTerm) {
//       results = results.filter(user =>
//         (user.username?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
//         (user.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
//         (getUserRoleDisplay(user)?.toLowerCase() || '').includes(searchTerm.toLowerCase())
//       );
//     }

//     // Apply sorting
//     if (sortConfig.key) {
//       results.sort((a, b) => {
//         let aVal, bVal;

//         // Handle role sorting specially
//         if (sortConfig.key === 'role') {
//           aVal = getUserRoleDisplay(a);
//           bVal = getUserRoleDisplay(b);
//         } else {
//           aVal = a[sortConfig.key];
//           bVal = b[sortConfig.key];
//         }
        
//         if (aVal === null || aVal === undefined) return 1;
//         if (bVal === null || bVal === undefined) return -1;
        
//         if (typeof aVal === 'string' && typeof bVal === 'string') {
//           return sortConfig.direction === 'asc' 
//             ? aVal.localeCompare(bVal)
//             : bVal.localeCompare(aVal);
//         }
        
//         if (aVal < bVal) {
//           return sortConfig.direction === 'asc' ? -1 : 1;
//         }
//         if (aVal > bVal) {
//           return sortConfig.direction === 'asc' ? 1 : -1;
//         }
//         return 0;
//       });
//     }

//     setFilteredUsers(results);
//   }, [users, searchTerm, sortConfig, roles]);

//   /** ----------------- SORT HANDLER ----------------- **/
//   const handleSort = (key) => {
//     let direction = 'asc';
//     if (sortConfig.key === key && sortConfig.direction === 'asc') {
//       direction = 'desc';
//     }
//     setSortConfig({ key, direction });
//   };

//   /** ----------------- FILTER HANDLER ----------------- **/
//   const handleFilterChange = (key, value) => {
//     setFilters(prev => ({
//       ...prev,
//       [key]: value
//     }));
//   };

//   /** ----------------- RESET FILTERS ----------------- **/
//   const resetFilters = () => {
//     setFilters({
//       role: ""
//     });
//     setSearchTerm("");
//     setSortConfig({ key: null, direction: 'asc' });
//   };

//   /** ----------------- FORM HANDLERS ----------------- **/
//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setForm(prev => ({ 
//       ...prev, 
//       [name]: value 
//     }));
//   };

//   const openDrawer = (user = null, view = false) => {
//     if (user) {
//       // Convert null values to empty strings for form
//       const formData = {};
//       Object.keys(form).forEach(key => {
//         formData[key] = user[key] !== null && user[key] !== undefined ? user[key] : "";
//       });
      
//       setForm(formData);
//       setEditingUser(user);
//       setViewMode(view);
//     } else {
//       setForm({
//         username: "",
//         email: "",
//         password: "",
//         confirmPassword: "",
//         role_id: ""
//       });
//       setEditingUser(null);
//       setViewMode(false);
//     }
//     setDrawerOpen(true);
//     setError(null);
//     setSuccess(null);
//   };

//   const closeDrawer = () => {
//     setDrawerOpen(false);
//     setEditingUser(null);
//     setViewMode(false);
//     setError(null);
//     setSuccess(null);
//   };

//   const validateForm = () => {
//     const requiredFields = ['username', 'email', 'role_id'];
//     const missingFields = requiredFields.filter(field => !form[field]?.trim());
    
//     if (missingFields.length > 0) {
//       setError(`Please fill in required fields: ${missingFields.map(f => f.replace('_', ' ')).join(', ')}`);
//       return false;
//     }
    
//     if (!editingUser && !form.password) {
//       setError('Password is required for new users');
//       return false;
//     }
    
//     if (form.password && form.password.length < 6) {
//       setError('Password must be at least 6 characters');
//       return false;
//     }
    
//     if (form.password !== form.confirmPassword) {
//       setError('Passwords do not match');
//       return false;
//     }
    
//     return true;
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setError(null);
//     setSuccess(null);

//     if (!validateForm()) {
//       return;
//     }

//     setSubmitting(true);

//     try {
//       const token = getToken();
//       if (!token) {
//         throw new Error("Authentication required. Please login.");
//       }

//       const method = editingUser ? "PUT" : "POST";
//       const url = editingUser 
//         ? (isAdmin() ? `${API_BASE_URL}/api/users/${editingUser.id}` : `${API_BASE_URL}/api/users/profile`)
//         : `${API_BASE_URL}/api/users/register`;

//       // Prepare data for submission
//       const submitData = { ...form };
      
//       // Remove confirmPassword from submission
//       delete submitData.confirmPassword;
      
//       // Convert empty strings to null
//       Object.keys(submitData).forEach(key => {
//         if (submitData[key] === '') {
//           submitData[key] = null;
//         }
//       });

//       // Only include password if provided
//       if (!submitData.password) {
//         delete submitData.password;
//       }

//       const res = await fetchWithTimeout(url, {
//         method,
//         headers: {
//           "Authorization": `Bearer ${token}`,
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify(submitData),
//       });

//       if (!res.ok) {
//         const errorData = await res.json();
//         throw new Error(errorData.error || errorData.message || "Operation failed");
//       }

//       const data = await res.json();
      
//       setSuccess(editingUser ? "User updated successfully!" : "User created successfully!");
      
//       await fetchUsers();
      
//       setTimeout(() => {
//         closeDrawer();
//       }, 1500);

//     } catch (err) {
//       console.error("Error saving user:", err);
//       setError(`Failed to save user: ${err.message}`);
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   const handleDelete = async (id) => {
//     if (!isAdmin() && currentUser?.id !== id) {
//       swal("Error", "You can only delete your own account", "error");
//       return;
//     }

//     const confirm = await swal({
//       title: "Are you sure?",
//       text: currentUser?.id === id 
//         ? "This will permanently delete your account and log you out immediately!" 
//         : "This will permanently delete the user!",
//       icon: "warning",
//       buttons: true,
//       dangerMode: true,
//     });

//     if (!confirm) return;

//     try {
//       const token = getToken();
//       const res = await fetchWithTimeout(`${API_BASE_URL}/api/users/${id}`, { 
//         method: "DELETE",
//         headers: { 
//           "Authorization": `Bearer ${token}`,
//           "Content-Type": "application/json"
//         }
//       });

//       if (!res.ok) {
//         throw new Error("Failed to delete user");
//       }

//       setSuccess("User deleted successfully!");
      
//       if (currentUser?.id === id) {
//         // User deleted their own account
//         localStorage.removeItem("token");
//         setIsAuthenticated(false);
//         setCurrentUser(null);
//         setTimeout(() => {
//           window.location.href = "/login";
//         }, 1500);
//       } else {
//         await fetchUsers();
//       }
      
//       setTimeout(() => setSuccess(null), 3000);
//     } catch (err) {
//       console.error("Error deleting user:", err);
//       setError(`Failed to delete user: ${err.message}`);
//     }
//   };

//   const handleView = (user) => {
//     const roleName = getUserRoleDisplay(user);
//     const details = `
// 👤 Username: ${user.username}
// 📧 Email: ${user.email || 'N/A'}
// 👥 Role: ${roleName || 'N/A'}

// 🆔 User ID: ${user.id}
// 📅 Created: ${user.created_at ? user.created_at.split('T')[0] : 'N/A'}
//     `.trim();

//     swal({
//       title: `User Details - ${user.username}`,
//       text: details,
//       icon: "info",
//       buttons: {
//         edit: {
//           text: "Edit",
//           value: "edit",
//         },
//         close: {
//           text: "Close",
//           value: "close",
//         }
//       }
//     }).then((value) => {
//       if (value === "edit") {
//         openDrawer(user);
//       }
//     });
//   };

//   const handleLogout = () => {
//     localStorage.removeItem("token");
//     setIsAuthenticated(false);
//     setCurrentUser(null);
//     window.location.href = "/login";
//   };

//   const getFieldIcon = (fieldName) => {
//     const icons = {
//       username: <FaUser size={14} />,
//       email: <FaEnvelope size={14} />,
//       role_id: <FaUser size={14} />
//     };
//     return icons[fieldName] || <FaUser size={14} />;
//   };

//   // Clear messages after timeout
//   useEffect(() => {
//     if (success) {
//       const timer = setTimeout(() => setSuccess(null), 5000);
//       return () => clearTimeout(timer);
//     }
//   }, [success]);

//   /** ----------------- STYLES ----------------- **/
//   const styles = {
//     page: {
//       minHeight: "100vh", 
//       backgroundColor: colors.light,
//       fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
//     },
//     container: {
//       maxWidth: "1400px", 
//       margin: "0 auto", 
//       padding: "20px" 
//     },
//     header: {
//       background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
//       borderRadius: "16px",
//       padding: "30px",
//       marginBottom: "30px",
//       color: "white",
//       boxShadow: "0 8px 25px rgba(67, 97, 238, 0.3)"
//     },
//     title: {
//       margin: "0 0 10px 0", 
//       fontSize: "clamp(1.8rem, 4vw, 2.5rem)",
//       fontWeight: "700"
//     },
//     subtitle: {
//       margin: 0, 
//       opacity: 0.9,
//       fontSize: "clamp(0.9rem, 2vw, 1.1rem)"
//     },
//     controls: {
//       display: "flex",
//       justifyContent: "space-between",
//       alignItems: "center",
//       marginBottom: "25px",
//       flexWrap: "wrap",
//       gap: "15px"
//     },
//     searchBox: {
//       padding: "10px 15px",
//       border: "1px solid #ddd",
//       borderRadius: "8px",
//       fontSize: "14px",
//       minWidth: "250px",
//       flex: "1"
//     },
//     filterSelect: {
//       padding: "10px 15px",
//       border: "1px solid #ddd",
//       borderRadius: "8px",
//       fontSize: "14px",
//       backgroundColor: "white",
//       minWidth: "150px"
//     },
//     actionButton: {
//       backgroundColor: colors.primary,
//       color: "white",
//       border: "none",
//       padding: "12px 24px",
//       borderRadius: "12px",
//       cursor: "pointer",
//       fontWeight: "600",
//       fontSize: "14px",
//       display: "flex",
//       alignItems: "center",
//       gap: "8px",
//       boxShadow: `0 4px 15px ${colors.primary}40`,
//       transition: "all 0.3s ease"
//     },
//     tableContainer: {
//       backgroundColor: colors.background,
//       borderRadius: "16px",
//       overflow: "hidden",
//       boxShadow: "0 4px 20px rgba(0,0,0,0.08)"
//     },
//     table: {
//       width: "100%",
//       borderCollapse: "collapse",
//       minWidth: "800px"
//     },
//     tableHeader: {
//       backgroundColor: colors.primary,
//       color: "white"
//     },
//     tableHeaderCell: {
//       padding: "16px 20px", 
//       textAlign: "left", 
//       fontWeight: "600",
//       fontSize: "14px",
//       cursor: "pointer",
//       userSelect: "none"
//     },
//     tableCell: {
//       padding: "16px 20px",
//       border: "none",
//       fontSize: "14px"
//     },
//     actionButtonSmall: {
//       background: "none", 
//       border: "none", 
//       cursor: "pointer",
//       padding: "8px",
//       borderRadius: "8px",
//       transition: "all 0.2s ease"
//     }
//   };

//   /** ----------------- RENDER ----------------- **/
//   if (!isAuthenticated) {
//     return (
//       <div style={styles.page}>
//         <div style={styles.container}>
//           <div style={{ 
//             textAlign: "center", 
//             padding: "60px 20px",
//             color: colors.textLight
//           }}>
//             <FaExclamationTriangle size={48} style={{ marginBottom: "20px", opacity: 0.5 }} />
//             <h2>Authentication Required</h2>
//             <p>Please login to access this page.</p>
//             <button
//               onClick={() => (window.location.href = "/login")}
//               style={styles.actionButton}
//             >
//               Go to Login
//             </button>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div style={styles.page}>
//       <div style={styles.container}>
        
//         {/* Header */}
//         <div style={styles.header}>
//           <h1 style={styles.title}>👥 User Management</h1>
//           <p style={styles.subtitle}>
//             Welcome, {currentUser?.username} ({currentUser?.role}) - Manage system users efficiently
//           </p>
//         </div>

//         {/* Success Banner */}
//         {success && (
//           <div style={{
//             backgroundColor: `${colors.success}15`,
//             border: `1px solid ${colors.success}40`,
//             color: colors.success,
//             padding: "16px 20px",
//             borderRadius: "12px",
//             marginBottom: "25px",
//             display: "flex",
//             alignItems: "center",
//             gap: "12px"
//           }}>
//             <FaSync />
//             <div style={{ flex: 1 }}>
//               {success}
//             </div>
//             <button
//               onClick={() => setSuccess(null)}
//               style={{
//                 background: "none",
//                 border: "none",
//                 color: colors.success,
//                 cursor: "pointer",
//                 padding: "4px"
//               }}
//             >
//               <FaTimes />
//             </button>
//           </div>
//         )}

//         {/* Error Banner */}
//         {error && (
//           <div style={{
//             backgroundColor: `${colors.error}15`,
//             border: `1px solid ${colors.error}40`,
//             color: colors.error,
//             padding: "16px 20px",
//             borderRadius: "12px",
//             marginBottom: "25px",
//             display: "flex",
//             alignItems: "center",
//             gap: "12px"
//           }}>
//             <FaExclamationTriangle />
//             <div style={{ flex: 1 }}>
//               {error}
//             </div>
//             <button
//               onClick={() => setError(null)}
//               style={{
//                 background: "none",
//                 border: "none",
//                 color: colors.error,
//                 cursor: "pointer",
//                 padding: "4px"
//               }}
//             >
//               <FaTimes />
//             </button>
//           </div>
//         )}

//         {/* Action Bar */}
//         <div style={styles.controls}>
//           <div style={{
//             display: "flex",
//             alignItems: "center",
//             gap: "10px",
//             flexWrap: "wrap"
//           }}>
//             <div style={{
//               padding: "8px 16px",
//               backgroundColor: colors.background,
//               borderRadius: "12px",
//               boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
//               color: colors.text,
//               fontWeight: "600"
//             }}>
//               Total: {users.length} users
//             </div>
            
//             <input
//               type="text"
//               placeholder="🔍 Search users..."
//               value={searchTerm}
//               onChange={(e) => setSearchTerm(e.target.value)}
//               style={styles.searchBox}
//             />
            
//             <select 
//               value={filters.role} 
//               onChange={(e) => handleFilterChange('role', e.target.value)}
//               style={styles.filterSelect}
//             >
//               <option value="">All Roles</option>
//               {roles.map(role => (
//                 <option key={role.id} value={role.name}>{role.name}</option>
//               ))}
//             </select>

//             <button
//               onClick={resetFilters}
//               style={{
//                 padding: "10px 15px",
//                 backgroundColor: colors.textLight,
//                 color: "white",
//                 border: "none",
//                 borderRadius: "8px",
//                 cursor: "pointer",
//                 fontSize: "14px"
//               }}
//             >
//               🔄 Reset
//             </button>
//           </div>
          
//           <div style={{ display: "flex", gap: "10px" }}>
//             <button
//               onClick={fetchUsers}
//               style={{
//                 ...styles.actionButton,
//                 backgroundColor: colors.success
//               }}
//               title="Refresh users"
//             >
//               <FaSync /> Refresh
//             </button>
            
//             {isAdmin() && (
//               <button
//                 onClick={() => openDrawer()}
//                 style={styles.actionButton}
//               >
//                 <FaPlus /> Add User
//               </button>
//             )}
            
//             <button
//               onClick={handleLogout}
//               style={{
//                 ...styles.actionButton,
//                 backgroundColor: colors.warning
//               }}
//             >
//               Logout
//             </button>
//           </div>
//         </div>

//         {/* Users Table */}
//         <div style={styles.tableContainer}>
//           {loading ? (
//             <div style={{ 
//               padding: "60px 20px", 
//               textAlign: "center", 
//               color: colors.textLight 
//             }}>
//               <FaSync size={24} style={{ animation: "spin 1s linear infinite", marginBottom: "16px" }} />
//               <div>Loading users...</div>
//             </div>
//           ) : error && users.length === 0 ? (
//             <div style={{ 
//               padding: "60px 20px", 
//               textAlign: "center", 
//               color: colors.textLight 
//             }}>
//               <FaExclamationTriangle size={32} style={{ marginBottom: "16px", opacity: 0.5 }} />
//               <div>Unable to load users. {error}</div>
//             </div>
//           ) : (
//             <div style={{ overflowX: "auto" }}>
//               <table style={styles.table}>
//                 <thead>
//                   <tr style={styles.tableHeader}>
//                     {['USERNAME', 'EMAIL', 'ROLE', 'ACTIONS'].map(header => (
//                       <th 
//                         key={header}
//                         style={styles.tableHeaderCell}
//                         onClick={() => handleSort(header.toLowerCase())}
//                       >
//                         {header} {sortConfig.key === header.toLowerCase() && 
//                           (sortConfig.direction === 'asc' ? '↑' : '↓')}
//                       </th>
//                     ))}
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {Array.isArray(filteredUsers) && filteredUsers.length > 0 ? (
//                     filteredUsers.map((user, index) => (
//                       <tr 
//                         key={user.id}
//                         style={{
//                           backgroundColor: index % 2 === 0 ? colors.background : "#f8f9fe",
//                           borderBottom: `1px solid ${colors.border}`,
//                           transition: "background-color 0.2s ease"
//                         }}
//                         onMouseEnter={(e) => {
//                           e.target.parentNode.style.backgroundColor = "#f0f2ff";
//                         }}
//                         onMouseLeave={(e) => {
//                           e.target.parentNode.style.backgroundColor = index % 2 === 0 ? colors.background : "#f8f9fe";
//                         }}
//                       >
//                         <td style={styles.tableCell}>
//                           <div>
//                             <div style={{ 
//                               fontWeight: "600", 
//                               color: colors.text,
//                               marginBottom: "4px"
//                             }}>
//                               {user.username}
//                             </div>
//                             {user.first_name && user.last_name && (
//                               <div style={{ 
//                                 fontSize: "12px", 
//                                 color: colors.textLight 
//                               }}>
//                                 {user.first_name} {user.last_name}
//                               </div>
//                             )}
//                           </div>
//                         </td>
//                         <td style={styles.tableCell}>{user.email}</td>
//                         <td style={styles.tableCell}>{getUserRoleDisplay(user)}</td>
//                         <td style={styles.tableCell}>
//                           <div style={{
//                             display: "flex",
//                             gap: "8px"
//                           }}>
//                             <button
//                               onClick={() => handleView(user)}
//                               style={{
//                                 ...styles.actionButtonSmall,
//                                 color: colors.primary,
//                               }}
//                               title="View Details"
//                               onMouseOver={(e) => {
//                                 e.target.style.backgroundColor = `${colors.primary}15`;
//                               }}
//                               onMouseOut={(e) => {
//                                 e.target.style.backgroundColor = "transparent";
//                               }}
//                             >
//                               <FaEye size={16} />
//                             </button>
//                             <button
//                               onClick={() => openDrawer(user)}
//                               style={{
//                                 ...styles.actionButtonSmall,
//                                 color: colors.success,
//                               }}
//                               title="Edit User"
//                               onMouseOver={(e) => {
//                                 e.target.style.backgroundColor = `${colors.success}15`;
//                               }}
//                               onMouseOut={(e) => {
//                                 e.target.style.backgroundColor = "transparent";
//                               }}
//                             >
//                               <FaEdit size={16} />
//                             </button>
//                             <button
//                               onClick={() => handleDelete(user.id)}
//                               style={{
//                                 ...styles.actionButtonSmall,
//                                 color: colors.warning,
//                               }}
//                               title="Delete User"
//                               onMouseOver={(e) => {
//                                 e.target.style.backgroundColor = `${colors.warning}15`;
//                               }}
//                               onMouseOut={(e) => {
//                                 e.target.style.backgroundColor = "transparent";
//                               }}
//                             >
//                               <FaTrash size={16} />
//                             </button>
//                           </div>
//                         </td>
//                       </tr>
//                     ))
//                   ) : (
//                     <tr>
//                       <td colSpan="4" style={{ 
//                         textAlign: "center", 
//                         padding: "60px 20px",
//                         color: colors.textLight
//                       }}>
//                         <div style={{ fontSize: "16px", marginBottom: "10px" }}>
//                           No users found
//                         </div>
//                         {isAdmin() && (
//                           <button
//                             onClick={() => openDrawer()}
//                             style={styles.actionButton}
//                           >
//                             <FaPlus /> Add Your First User
//                           </button>
//                         )}
//                       </td>
//                     </tr>
//                   )}
//                 </tbody>
//               </table>
//             </div>
//           )}
//         </div>
//       </div>

//       {/* Drawer */}
//       {drawerOpen && (
//         <>
//           {/* Overlay */}
//           <div
//             style={{
//               position: "fixed",
//               top: 0,
//               left: 0,
//               right: 0,
//               bottom: 0,
//               backgroundColor: "rgba(0, 0, 0, 0.5)",
//               zIndex: 1000,
//             }}
//             onClick={closeDrawer}
//           />
          
//           {/* Drawer Content */}
//           <div
//             style={{
//               position: "fixed",
//               top: 0,
//               right: 0,
//               width: "min(90%, 500px)",
//               height: "100%",
//               backgroundColor: colors.background,
//               boxShadow: "-4px 0 25px rgba(0,0,0,0.15)",
//               padding: "0",
//               overflowY: "auto",
//               transition: "all 0.3s ease-in-out",
//               zIndex: 1001,
//               display: "flex",
//               flexDirection: "column"
//             }}
//           >
//             {/* Drawer Header */}
//             <div style={{
//               padding: "25px 25px 20px",
//               borderBottom: `1px solid ${colors.border}`,
//               background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
//               color: "white"
//             }}>
//               <div style={{
//                 display: "flex",
//                 justifyContent: "space-between",
//                 alignItems: "center",
//                 marginBottom: "10px"
//               }}>
//                 <h2 style={{ margin: 0, fontSize: "1.5rem" }}>
//                   {viewMode ? "User Details" : editingUser ? "Edit User" : "Add User"}
//                 </h2>
//                 <button
//                   onClick={closeDrawer}
//                   style={{
//                     background: "none",
//                     border: "none",
//                     color: "white",
//                     cursor: "pointer",
//                     padding: "8px",
//                     borderRadius: "8px",
//                     transition: "background-color 0.2s ease"
//                   }}
//                   onMouseOver={(e) => {
//                     e.target.style.backgroundColor = "rgba(255,255,255,0.2)";
//                   }}
//                   onMouseOut={(e) => {
//                     e.target.style.backgroundColor = "transparent";
//                   }}
//                 >
//                   <FaTimes size={20} />
//                 </button>
//               </div>
//               <p style={{ margin: 0, opacity: 0.9, fontSize: "14px" }}>
//                 {viewMode ? "View user information" : 
//                  editingUser ? "Update user details" : "Add a new system user"}
//               </p>
//             </div>

//             {/* Drawer Body */}
//             <div style={{ padding: "25px", flex: 1 }}>
//               <form onSubmit={handleSubmit}>
//                 <div style={{
//                   display: "grid",
//                   gap: "20px"
//                 }}>
//                   {['username', 'email', 'role_id'].map((field) => {
//                     const readOnly = viewMode;
//                     const isRequired = ['username', 'email', 'role_id'].includes(field);
//                     const isPassword = field === 'password';

//                     return (
//                       <div key={field}>
//                         <label style={{ 
//                           display: "flex",
//                           alignItems: "center",
//                           gap: "8px",
//                           marginBottom: "6px", 
//                           fontWeight: "600",
//                           color: colors.text,
//                           fontSize: "14px"
//                         }}>
//                           {getFieldIcon(field)}
//                           {field.split('_').map(word => 
//                             word.charAt(0).toUpperCase() + word.slice(1)
//                           ).join(' ')}
//                           {isRequired && <span style={{ color: colors.error }}>*</span>}
//                         </label>
                        
//                         {field === 'role_id' ? (
//                           <select
//                             name={field}
//                             value={form[field] || ""}
//                             onChange={handleChange}
//                             disabled={readOnly || (!isAdmin() && editingUser && currentUser?.id !== editingUser.id)}
//                             style={{
//                               width: "100%",
//                               padding: "12px",
//                               border: `1px solid ${colors.border}`,
//                               borderRadius: "8px",
//                               backgroundColor: (readOnly || (!isAdmin() && editingUser && currentUser?.id !== editingUser.id)) ? "#f8f9fa" : "white",
//                               color: colors.text,
//                               fontSize: "14px",
//                               transition: "all 0.2s ease"
//                             }}
//                           >
//                             <option value="">Select Role</option>
//                             {roles.map(role => (
//                               <option key={role.id} value={role.id}>{role.name}</option>
//                             ))}
//                           </select>
//                         ) : (
//                           <input
//                             type={isPassword ? "password" : "text"}
//                             name={field}
//                             value={form[field] || ""}
//                             onChange={handleChange}
//                             readOnly={readOnly}
//                             disabled={readOnly || (!isAdmin() && editingUser && currentUser?.id !== editingUser.id)}
//                             placeholder={isRequired ? "Required" : ""}
//                             style={{
//                               width: "100%",
//                               padding: "12px",
//                               border: `1px solid ${colors.border}`,
//                               borderRadius: "8px",
//                               backgroundColor: (readOnly || (!isAdmin() && editingUser && currentUser?.id !== editingUser.id)) ? "#f8f9fa" : "white",
//                               color: colors.text,
//                               fontSize: "14px",
//                               transition: "all 0.2s ease",
//                               cursor: readOnly ? "not-allowed" : "text"
//                             }}
//                             onFocus={(e) => {
//                               if (!readOnly) {
//                                 e.target.style.borderColor = colors.primary;
//                                 e.target.style.boxShadow = `0 0 0 3px ${colors.primary}20`;
//                               }
//                             }}
//                             onBlur={(e) => {
//                               if (!readOnly) {
//                                 e.target.style.borderColor = colors.border;
//                                 e.target.style.boxShadow = "none";
//                               }
//                             }}
//                           />
//                         )}
//                       </div>
//                     );
//                   })}

//                   {/* Password Fields (only for add/edit, not view) */}
//                   {!viewMode && (
//                     <>
//                       <div>
//                         <label style={{ 
//                           display: "flex",
//                           alignItems: "center",
//                           gap: "8px",
//                           marginBottom: "6px", 
//                           fontWeight: "600",
//                           color: colors.text,
//                           fontSize: "14px"
//                         }}>
//                           <FaUser size={14} />
//                           Password
//                           {!editingUser && <span style={{ color: colors.error }}>*</span>}
//                         </label>
//                         <input
//                           type="password"
//                           name="password"
//                           value={form.password || ""}
//                           onChange={handleChange}
//                           placeholder={!editingUser ? "Required" : "Leave blank to keep current"}
//                           style={{
//                             width: "100%",
//                             padding: "12px",
//                             border: `1px solid ${colors.border}`,
//                             borderRadius: "8px",
//                             backgroundColor: "white",
//                             color: colors.text,
//                             fontSize: "14px",
//                             transition: "all 0.2s ease"
//                           }}
//                           onFocus={(e) => {
//                             e.target.style.borderColor = colors.primary;
//                             e.target.style.boxShadow = `0 0 0 3px ${colors.primary}20`;
//                           }}
//                           onBlur={(e) => {
//                             e.target.style.borderColor = colors.border;
//                             e.target.style.boxShadow = "none";
//                           }}
//                         />
//                       </div>

//                       <div>
//                         <label style={{ 
//                           display: "flex",
//                           alignItems: "center",
//                           gap: "8px",
//                           marginBottom: "6px", 
//                           fontWeight: "600",
//                           color: colors.text,
//                           fontSize: "14px"
//                         }}>
//                           <FaUser size={14} />
//                           Confirm Password
//                           {!editingUser && <span style={{ color: colors.error }}>*</span>}
//                         </label>
//                         <input
//                           type="password"
//                           name="confirmPassword"
//                           value={form.confirmPassword || ""}
//                           onChange={handleChange}
//                           placeholder={!editingUser ? "Required" : "Confirm new password"}
//                           style={{
//                             width: "100%",
//                             padding: "12px",
//                             border: `1px solid ${colors.border}`,
//                             borderRadius: "8px",
//                             backgroundColor: "white",
//                             color: colors.text,
//                             fontSize: "14px",
//                             transition: "all 0.2s ease"
//                           }}
//                           onFocus={(e) => {
//                             e.target.style.borderColor = colors.primary;
//                             e.target.style.boxShadow = `0 0 0 3px ${colors.primary}20`;
//                           }}
//                           onBlur={(e) => {
//                             e.target.style.borderColor = colors.border;
//                             e.target.style.boxShadow = "none";
//                           }}
//                         />
//                       </div>
//                     </>
//                   )}
//                 </div>

//                 {!viewMode && (
//                   <button
//                     type="submit"
//                     disabled={submitting}
//                     style={{
//                       backgroundColor: submitting ? colors.textLight : colors.primary,
//                       color: "white",
//                       padding: "14px",
//                       border: "none",
//                       borderRadius: "8px",
//                       cursor: submitting ? "not-allowed" : "pointer",
//                       width: "100%",
//                       fontWeight: "600",
//                       fontSize: "15px",
//                       marginTop: "30px",
//                       transition: "all 0.3s ease",
//                       display: "flex",
//                       alignItems: "center",
//                       justifyContent: "center",
//                       gap: "8px"
//                     }}
//                     onMouseOver={(e) => {
//                       if (!submitting) {
//                         e.target.style.backgroundColor = colors.secondary;
//                         e.target.style.transform = "translateY(-2px)";
//                       }
//                     }}
//                     onMouseOut={(e) => {
//                       if (!submitting) {
//                         e.target.style.backgroundColor = colors.primary;
//                         e.target.style.transform = "translateY(0)";
//                       }
//                     }}
//                   >
//                     {submitting ? (
//                       <>
//                         <FaSync className="spin" size={16} />
//                         {editingUser ? "Updating..." : "Creating..."}
//                       </>
//                     ) : (
//                       <>
//                         <FaEdit size={16} />
//                         {editingUser ? "Update User" : "Create User"}
//                       </>
//                     )}
//                   </button>
//                 )}
//               </form>
//             </div>
//           </div>
//         </>
//       )}

//       {/* Add CSS for spinner animation - FIXED: removed jsx attribute */}
//       <style>{`
//         @keyframes spin {
//           0% { transform: rotate(0deg); }
//           100% { transform: rotate(360deg); }
//         }
//         .spin {
//           animation: spin 1s linear infinite;
//         }
//       `}</style>
//     </div>
//   );
// };

// export default Users;